import { type FieldValues, useController } from 'react-hook-form';

import type { ComponentProps } from '~/chords/ComponentProps';
import { FormField, type FormFieldProps } from '~/chords/FormField';
import {
  DropdownTreeSingleSelect,
  type DropdownTreeSingleSelectProps,
} from '~/components/DropdownTreeSingleSelect/DropdownTreeSingleSelect';

export type FormFieldDropdownTreeSingleSelectProps<
  TOptionProps,
  TFormFields extends FieldValues,
> = Omit<FormFieldProps<TFormFields>, 'children'> &
  Omit<
    DropdownTreeSingleSelectProps<TOptionProps>,
    'name' | 'className' | 'value' | 'onValueChange'
  > & {
    onValueChange?: (value: string) => void;
  };

export function FormFieldDropdownTreeSingleSelect<
  TOptionProps = unknown,
  TFormFields extends FieldValues = FieldValues,
>({
  name,
  label,
  dangerouslyOverrideClassName,
  disabled = false,
  readOnly = false,
  labelAddon,
  fieldMessageClassName,
  onValueChange,
  showErrorMessage = true,
  ...dropdownProps
}: ComponentProps<
  FormFieldDropdownTreeSingleSelectProps<TOptionProps, TFormFields>
>) {
  const {
    field: { ref: _ref, ...fieldWithoutRef },
    fieldState: { invalid },
  } = useController({ name, disabled });

  return (
    <FormField
      name={name}
      label={label}
      dangerouslyOverrideClassName={dangerouslyOverrideClassName}
      disabled={disabled}
      readOnly={readOnly}
      labelAddon={labelAddon}
      fieldMessageClassName={fieldMessageClassName}
      showErrorMessage={showErrorMessage}
    >
      <DropdownTreeSingleSelect
        {...fieldWithoutRef}
        id={name}
        onValueChange={(value) => {
          fieldWithoutRef.onChange(value);
          onValueChange?.(value);
        }}
        disabled={disabled || readOnly}
        state={invalid ? 'error' : 'default'}
        {...dropdownProps}
        clearOptions={{
          ...(dropdownProps?.clearOptions ?? {}),
          onClear: () => {
            fieldWithoutRef.onChange('');
            dropdownProps?.clearOptions?.onClear?.();
          },
        }}
      />
    </FormField>
  );
}

FormFieldDropdownTreeSingleSelect.displayName =
  'FormFieldDropdownTreeSingleSelect';
